import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { BATCH_SIZE, DEFAULT_LIST_KEY, chunk, listToMakeDefault, slugKey } from '../utils/catalog';
import { isTaxKind } from '../utils/billing';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
// PostgREST devuelve como máximo 1000 filas por consulta: se pide por páginas hasta traer todo.
const PAGE = 1000;

// Solo «la tabla no existe»: antes bastaba que el mensaje nombrara «products» y cualquier error (permisos, columnas) decía «falta la migración».
const isMissingTable = (error) =>
  ['42P01', 'PGRST205'].includes(error?.code) || /relation .*(products|price_lists|product_prices).* does not exist|could not find the table/i.test(error?.message || '');

// Solo cuando la base dice que la columna NO existe (PGRST204 / 42703). Un «null value in column tax_kind … violates not-null» NO es una columna que falte.
const isMissingTaxColumn = (error) => ['PGRST204', '42703'].includes(error?.code) && /tax_kind|tax_rate/i.test(error?.message || '');

// Qué decirle a quien importa según lo que respondió la base (antes todo error decía «falta la migración»).
export const importErrorMessage = (error) => {
  if (isMissingTable(error)) return 'Falta ejecutar la migración 20261003a_catalog.sql en Supabase.';
  if (error?.code === '42501' || /row-level security|permission denied/i.test(error?.message || '')) {
    return 'La base no te dejó guardar productos en este negocio (permisos). Entra con el correo del dueño o de un administrador del sistema.';
  }
  const detail = String(error?.message || '').slice(0, 160);
  return `La importación se detuvo a medias${detail ? ` (${detail})` : ''}. Revisa el catálogo y vuelve a importar el archivo: no duplica.`;
};

const fetchAll = async (table, business, order) => {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase.from(table).select('*').eq('business_id', business).order(order).range(from, from + PAGE - 1);
    if (error) return { error };
    rows.push(...(data || []));
    if (!data || data.length < PAGE) return { data: rows };
  }
};

// Catálogo de productos con listas de precios. Aquí se leen, importan y editan; el bot los consulta desde n8n.
export const useCatalog = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';

  const [products, setProducts] = useState([]);
  const [lists, setLists] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId) { setLoading(false); return; }
    setLoading(true);
    const business = String(activeBusinessId);
    const [p, l, pr] = await Promise.all([
      fetchAll('products', business, 'name'),
      supabase.from('price_lists').select('*').eq('business_id', business).order('created_at'),
      fetchAll('product_prices', business, 'product_id')
    ]);
    const error = p.error || l.error || pr.error;
    if (error) {
      setTableMissing(isMissingTable(error));
      setProducts([]); setLists([]); setPrices([]);
    } else {
      setTableMissing(false);
      setProducts(p.data); setLists(l.data || []); setPrices(pr.data);
    }
    setLoading(false);
  }, [activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  // product_id → { clave_de_lista: precio }
  const priceMap = useMemo(() => {
    const m = new Map();
    prices.forEach((x) => {
      if (!m.has(x.product_id)) m.set(x.product_id, {});
      m.get(x.product_id)[x.price_list_key] = Number(x.price);
    });
    return m;
  }, [prices]);

  const defaultList = useMemo(() => lists.find((l) => l.is_default) || lists[0] || null, [lists]);

  // Garantiza que existan las listas (por clave); si el negocio no tenía ninguna, la primera queda como predeterminada.
  const ensureLists = async (wanted) => {
    const business = String(activeBusinessId);
    const missing = wanted.filter((w) => !lists.some((l) => l.key === w.key));
    if (!missing.length) return true;
    const makeDefault = lists.length === 0;
    const rows = missing.map((w, i) => ({ business_id: business, key: w.key, name: w.name, is_default: makeDefault && i === 0 }));
    const { error } = await supabase.from('price_lists').insert(rows);
    if (error) { toast.error('No se pudieron crear las listas de precios.'); return false; }
    return true;
  };

  // Importa lo que devolvió buildImport: productos por (negocio, código) y sus precios, en lotes.
  const importProducts = async ({ products: items, lists: wantedLists }, { withTax = false } = {}) => {
    if (guardDemo()) return false;
    setImporting(true);
    try {
      const business = String(activeBusinessId);
      if (!(await ensureLists(wantedLists))) return false;
      const now = new Date().toISOString();
      let saved = 0;
      let taxStored = withTax;
      for (const part of chunk(items, BATCH_SIZE)) {
        const buildRows = (withTaxColumns) => part.map((it) => ({
          business_id: business, sku: it.sku, name: it.name, category: it.category, unit: it.unit,
          stock: it.stock, active: it.active, source: it.source || 'csv', updated_at: now,
          // El impuesto solo se guarda si el módulo de cotizaciones está encendido (sus columnas las crea esa migración).
          // Todas las filas llevan las dos columnas: en un envío por lotes, las filas sin ellas se guardan como NULL y la base las rechaza (son NOT NULL).
          // Un producto sin impuesto en el archivo queda como siempre: IVA 0 (el precio se da tal cual).
          ...(withTaxColumns ? { tax_kind: it.tax_kind ?? 'iva', tax_rate: it.tax_rate ?? 0 } : {})
        }));
        let { data, error } = await supabase.from('products').upsert(buildRows(taxStored), { onConflict: 'business_id,sku' }).select('id, sku');
        // Si la base aún no tiene las columnas de impuesto (migración 20261003b), se importa igual sin el IVA aparte y se avisa.
        if (error && taxStored && isMissingTaxColumn(error)) {
          taxStored = false;
          ({ data, error } = await supabase.from('products').upsert(buildRows(false), { onConflict: 'business_id,sku' }).select('id, sku'));
        }
        if (error) throw error;
        const idBySku = new Map(data.map((r) => [r.sku, r.id]));
        const priceRows = [];
        part.forEach((it) => {
          const id = idBySku.get(it.sku);
          if (!id) return;
          Object.entries(it.prices).forEach(([key, price]) => priceRows.push({ product_id: id, business_id: business, price_list_key: key, price, updated_at: now }));
        });
        if (priceRows.length) {
          const res = await supabase.from('product_prices').upsert(priceRows, { onConflict: 'product_id,price_list_key' });
          if (res.error) throw res.error;
        }
        saved += part.length;
      }
      // La lista predeterminada tiene que traer precios; si quedó vacía (otra lista recibió los precios), pasa a la primera que sí los trae.
      try {
        const { data: current } = await supabase.from('price_lists').select('key, is_default').eq('business_id', business);
        const makeDefault = listToMakeDefault({ lists: current || [], existingPricedKeys: new Set(prices.map((x) => x.price_list_key)), importedProducts: items });
        if (makeDefault) {
          await supabase.from('price_lists').update({ is_default: false }).eq('business_id', business).eq('is_default', true);
          await supabase.from('price_lists').update({ is_default: true }).eq('business_id', business).eq('key', makeDefault);
          toast(`La lista «${makeDefault}» quedó como predeterminada porque la anterior no tenía precios.`, { icon: 'ℹ️', duration: 6000 });
        }
      } catch (e) {
        console.warn('No se pudo ajustar la lista predeterminada:', e);
      }
      toast.success(`Listo: ${saved} producto${saved === 1 ? '' : 's'} importado${saved === 1 ? '' : 's'}.`);
      if (withTax && !taxStored) toast('Se importó sin el IVA aparte: falta ejecutar la migración 20261003b_billing_base.sql en Supabase y volver a importar el archivo.', { icon: '⚠️', duration: 8000 });
      await load();
      return true;
    } catch (e) {
      toast.error(importErrorMessage(e));
      await load();
      return false;
    } finally {
      setImporting(false);
    }
  };

  // Crea o edita un producto con sus precios por lista (un precio vacío borra el de esa lista).
  const saveProduct = async ({ id, sku, name, category, unit, stock, active = true, prices: byList = {}, tax_rate, tax_kind }) => {
    if (guardDemo()) return false;
    if (!String(name ?? '').trim()) { toast.error('Escribe el nombre del producto.'); return false; }
    const business = String(activeBusinessId);
    const cleanSku = String(sku ?? '').trim() || null;
    const stockNum = stock === '' || stock === null || stock === undefined ? null : Number(stock);
    if (stockNum !== null && (!Number.isFinite(stockNum) || stockNum < 0)) { toast.error('Las existencias deben ser un número igual o mayor que 0.'); return false; }
    const entries = Object.entries(byList);
    for (const [, v] of entries) {
      if (v !== '' && v !== null && v !== undefined && (!Number.isFinite(Number(v)) || Number(v) < 0)) { toast.error('Los precios deben ser números iguales o mayores que 0.'); return false; }
    }
    if (!entries.some(([, v]) => v !== '' && v !== null && v !== undefined)) { toast.error('Ponle al menos un precio.'); return false; }
    // El impuesto solo se envía cuando la pantalla lo muestra (módulo Cotizaciones): sin esa migración la columna no existe.
    const hasTax = tax_rate !== undefined || tax_kind !== undefined;
    const taxRate = tax_rate === '' || tax_rate === undefined || tax_rate === null ? 0 : Number(tax_rate);
    if (hasTax && (!Number.isFinite(taxRate) || taxRate < 0 || taxRate > 100)) { toast.error('El impuesto va de 0 a 100 %.'); return false; }
    if (hasTax && tax_kind !== undefined && !isTaxKind(tax_kind)) { toast.error('Tipo de impuesto no válido.'); return false; }

    const row = {
      business_id: business, sku: cleanSku, name: name.trim().slice(0, 200), category: String(category ?? '').trim() || null,
      unit: String(unit ?? '').trim() || null, stock: stockNum, active: Boolean(active), updated_at: new Date().toISOString(),
      ...(hasTax ? { tax_rate: taxRate, tax_kind: tax_kind || 'iva' } : {})
    };
    let productId = id;
    if (id) {
      const { error } = await supabase.from('products').update(row).eq('id', id);
      if (error) { toast.error(error.code === '23505' ? 'Ya hay otro producto con ese código.' : 'No se pudo guardar el producto.'); return false; }
    } else {
      const { data, error } = await supabase.from('products').insert([{ ...row, source: 'manual' }]).select('id').single();
      if (error) { toast.error(error.code === '23505' ? 'Ya hay otro producto con ese código.' : 'No se pudo guardar el producto.'); return false; }
      productId = data.id;
    }
    const upserts = []; const removes = [];
    entries.forEach(([key, v]) => {
      if (v === '' || v === null || v === undefined) removes.push(key);
      else upserts.push({ product_id: productId, business_id: business, price_list_key: key, price: Number(v), updated_at: row.updated_at });
    });
    if (upserts.length) {
      const { error } = await supabase.from('product_prices').upsert(upserts, { onConflict: 'product_id,price_list_key' });
      if (error) { toast.error('El producto se guardó, pero no sus precios. Intenta de nuevo.'); await load(); return false; }
    }
    if (removes.length) await supabase.from('product_prices').delete().eq('product_id', productId).in('price_list_key', removes);
    toast.success('Producto guardado');
    await load();
    return true;
  };

  const toggleProduct = async (id, active) => {
    if (guardDemo()) return false;
    const previous = products;
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, active } : p)));
    const { error } = await supabase.from('products').update({ active, updated_at: new Date().toISOString() }).eq('id', id);
    if (error) { setProducts(previous); toast.error('No se pudo guardar el cambio.'); return false; }
    return true;
  };

  const deleteProduct = async (id) => {
    if (guardDemo()) return false;
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) { toast.error('No se pudo eliminar el producto.'); return false; }
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setPrices((prev) => prev.filter((x) => x.product_id !== id));
    toast.success('Producto eliminado');
    return true;
  };

  const addList = async (name) => {
    if (guardDemo()) return false;
    const clean = String(name ?? '').trim().slice(0, 60);
    const key = slugKey(clean) || '';
    if (!clean || !key) { toast.error('Escribe el nombre de la lista (ej. Mayorista).'); return false; }
    if (lists.some((l) => l.key === key)) { toast.error('Ya hay una lista con ese nombre.'); return false; }
    const { error } = await supabase.from('price_lists').insert([{ business_id: String(activeBusinessId), key, name: clean, is_default: lists.length === 0 }]);
    if (error) { toast.error('No se pudo crear la lista.'); return false; }
    toast.success('Lista creada');
    await load();
    return true;
  };

  // Cambia la lista predeterminada: primero se quita la actual (solo puede haber una) y luego se marca la nueva.
  const setDefaultList = async (key) => {
    if (guardDemo()) return false;
    const business = String(activeBusinessId);
    const first = await supabase.from('price_lists').update({ is_default: false }).eq('business_id', business).eq('is_default', true);
    if (first.error) { toast.error('No se pudo cambiar la lista predeterminada.'); return false; }
    const { error } = await supabase.from('price_lists').update({ is_default: true }).eq('business_id', business).eq('key', key);
    if (error) { toast.error('No se pudo cambiar la lista predeterminada.'); await load(); return false; }
    await load();
    return true;
  };

  // Una lista con precios o con clientes asignados no se borra: el bot dejaría de poder dar esos precios.
  const deleteList = async (key) => {
    if (guardDemo()) return false;
    const target = lists.find((l) => l.key === key);
    if (!target) return false;
    if (target.is_default) { toast.error('No puedes borrar la lista predeterminada. Marca otra antes.'); return false; }
    if (prices.some((x) => x.price_list_key === key)) { toast.error('Esa lista todavía tiene precios. Elimina los precios (o los productos) antes.'); return false; }
    const business = String(activeBusinessId);
    const used = await supabase.from('customers').select('id', { count: 'exact', head: true }).eq('business_id', business).eq('price_list_key', key);
    if (used.error) { toast.error('No se pudo comprobar si hay clientes con esa lista.'); return false; }
    if ((used.count || 0) > 0) { toast.error(`Hay ${used.count} cliente(s) con esa lista asignada.`); return false; }
    const { error } = await supabase.from('price_lists').delete().eq('business_id', business).eq('key', key);
    if (error) { toast.error('No se pudo eliminar la lista.'); return false; }
    toast.success('Lista eliminada');
    await load();
    return true;
  };

  return {
    products, lists, priceMap, defaultList, loading, tableMissing, importing, isReadOnlyDemo, defaultKey: defaultList?.key || DEFAULT_LIST_KEY,
    reload: load, importProducts, saveProduct, toggleProduct, deleteProduct, addList, setDefaultList, deleteList
  };
};
